import { Link } from "react-router-dom";
import { Phone, Mail, Clock, ArrowRight, MapPin, Instagram } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";
import { siteConfig } from "@/lib/site-config";
import { services } from "@/lib/services-data";
import { locations } from "@/lib/locations-data";
import Logo from "@/components/Logo";

const Footer = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <footer className="bg-[#0b0b0b] text-white">
      {/* CTA Banner */}
      <div className="bg-primary">
        <div className="container-max px-4 py-12 md:py-14">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div>
              <h3 className="font-heading text-2xl md:text-3xl text-primary-foreground font-bold tracking-tight">
                Ready to Start Your Project?
              </h3>
              <p className="text-primary-foreground text-sm md:text-base mt-1">
                Free written estimates across metro Atlanta · {siteConfig.hours.summary}
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button
                size="lg"
                onClick={openQuoteForm}
                /* Deeper brand red: white on the lighter --secondary only
                   reaches 4.05:1 against this banner, which fails AA. */
                className="gap-2 rounded-xl font-bold bg-primary-dark text-white hover:bg-primary-dark/90"
              >
                Get Free Estimate <ArrowRight className="h-5 w-5" />
              </Button>
              <a href={siteConfig.phoneHref} className="inline-flex w-fit">
                <Button
                  size="lg"
                  className="gap-2 rounded-xl font-bold bg-white text-primary hover:bg-white/90"
                >
                  <Phone className="h-4 w-4" /> {siteConfig.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>

      <div className="container-max section-padding">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Logo
              src={siteConfig.logoLight}
              alt={siteConfig.name}
              className="h-16 w-auto mb-5"
              loading="lazy"
            />
            <p className="text-white/70 text-sm leading-relaxed">
              Remodeling and painting contractors based in {siteConfig.address.city},{" "}
              {siteConfig.address.state}. Nine trades in-house, six years of experience, and one
              crew from demo to final coat.
            </p>
            <div className="mt-4 bg-secondary/15 border border-secondary/30 rounded-xl px-4 py-3">
              <p className="text-white font-semibold text-sm">
                🎁 {siteConfig.discount.label}
              </p>
            </div>
            {siteConfig.social.instagram && (
              <a
                href={siteConfig.social.instagram}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-4 inline-flex items-center gap-2 text-white/75 hover:text-secondary text-sm font-medium transition-colors"
              >
                <Instagram className="h-4 w-4" /> {siteConfig.social.instagramHandle}
              </a>
            )}
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Services
            </h4>
            <ul className="space-y-2.5">
              {services.map((s) => (
                <li key={s.slug}>
                  <Link
                    to={`/services/${s.slug}`}
                    className="footer-link-underline text-white/70 hover:text-white text-sm transition-colors"
                  >
                    {s.title}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Service Areas
            </h4>
            <ul className="grid grid-cols-2 gap-x-2 gap-y-2 text-sm">
              {locations.slice(0, 10).map((loc) => (
                <li key={loc.slug}>
                  <Link
                    to={`/locations/${loc.slug}`}
                    className="footer-link-underline text-white/70 hover:text-white transition-colors"
                  >
                    {loc.name}
                  </Link>
                </li>
              ))}
            </ul>
            <Link
              to="/locations"
              className="mt-3 inline-block footer-link-underline text-secondary hover:text-secondary/80 text-sm font-semibold transition-colors"
            >
              All Locations<span className="sr-only"> we serve</span> →
            </Link>
            <h4 className="font-heading text-lg tracking-tight mt-7 mb-3 text-white font-bold">
              Quick Links
            </h4>
            <ul className="space-y-2 text-sm">
              {[
                { label: "Gallery", to: "/gallery" },
                { label: "Blog", to: "/blog" },
                { label: "About", to: "/about" },
              ].map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="footer-link-underline text-white/70 hover:text-white transition-colors"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-heading text-lg tracking-tight mb-4 text-white font-bold">
              Contact
            </h4>
            <ul className="space-y-3 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Phone className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a href={siteConfig.phoneHref} className="hover:text-white transition-colors">
                  {siteConfig.phone}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <Mail className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <a href={siteConfig.emailHref} className="hover:text-white transition-colors break-all">
                  {siteConfig.email}
                </a>
              </li>
              <li className="flex items-start gap-2">
                <MapPin className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <address className="not-italic">
                  {siteConfig.address.street}
                  <br />
                  {siteConfig.address.city}, {siteConfig.address.state} {siteConfig.address.postalCode}
                </address>
              </li>
            </ul>

            <h4 className="font-heading text-lg tracking-tight mt-7 mb-3 text-white font-bold">
              Business Hours
            </h4>
            <ul className="space-y-2 text-sm text-white/75">
              <li className="flex items-start gap-2">
                <Clock className="h-4 w-4 shrink-0 text-secondary mt-0.5" />
                <div>
                  <div>{siteConfig.hours.days}</div>
                  <div className="text-white/80 text-xs">{siteConfig.hours.time}</div>
                  <div className="text-white/50 text-xs mt-1">{siteConfig.hours.closed}</div>
                </div>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-white/75 text-xs">
            © {new Date().getFullYear()} {siteConfig.legalName}. All rights reserved.
          </p>
          <p className="text-white/75 text-xs">
            Remodeling &amp; Painting · Lawrenceville &amp; Metro Atlanta, GA
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
